import { useSlimAccount } from "@/api/hooks/account";
import { useUser } from "@/api/hooks/user";
import { useAuth } from "@/auth";
import { useAppSession } from "@/auth/AppSessionContext";
import { getFeatureValue } from "@/types/featureSet";
import { useFlag } from "@openfeature/react-sdk";
import { type ReactNode, useEffect, useState } from "react";
import { useNotificationSnapshot } from "../hooks";
import { useOpenNotification } from "../hooks/useOpenNotification";
import { NotificationToast } from "../notification-toast";
import { NotificationContext } from "./context";

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const { sessionActivated, invalid } = useAppSession();
  const { value: flag } = useFlag("push-notifications", false);
  const authenticated = Boolean(session?.token && sessionActivated && !invalid);
  const { user, features } = useUser(authenticated);
  const { data: account } = useSlimAccount(authenticated);
  const enabled =
    authenticated && flag === true && getFeatureValue(features, "tools.alert.read") === true && !!user && !!account;
  // Keep the same wrapper while flags and account data load, so the app below does not remount.
  return (
    <NotificationRuntime
      enabled={enabled}
      userId={enabled ? user?.id : undefined}
      accountId={enabled ? account?.id : undefined}
    >
      {children}
    </NotificationRuntime>
  );
}

function NotificationRuntime({
  enabled,
  userId,
  accountId,
  children,
}: {
  enabled: boolean;
  userId: number | undefined;
  accountId: number | undefined;
  children: ReactNode;
}) {
  const query = useNotificationSnapshot(userId, accountId, enabled);
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [userId, accountId, enabled]);
  return (
    <NotificationContext.Provider value={enabled ? { query, open, setOpen } : null}>
      {children}
      {enabled && <NotificationToastRuntime query={query} />}
    </NotificationContext.Provider>
  );
}

function NotificationToastRuntime({ query }: { query: ReturnType<typeof useNotificationSnapshot> }) {
  const openAlert = useOpenNotification();
  if (!query.toast.length) {
    return null;
  }

  return (
    <NotificationToast
      items={query.toast}
      onDismiss={query.dismissToast}
      onOpen={(item) => {
        query.markRead(item.id);
        if (!item.has_destination) {
          query.dismissToast(item.id);
          return;
        }
        void openAlert(item)
          .then((opened) => {
            if (opened) {
              query.dismissToast(item.id);
            }
          })
          .catch(() => undefined);
      }}
    />
  );
}
